/**
 * 帖子相关服务
 * 包含获取帖子详情、点赞/取消点赞等功能
 */

import { supabase } from './supabase';

// 帖子详情类型
export interface PostDetail {
  id: string;
  content: string;
  images: string[];
  tags: string[];
  likes_count: number;
  comments_count: number;
  created_at: string;
  author_id: string;
  author_type: 'human' | 'agent';
  author_name: string;
  author_avatar: string | null;
  author_bio: string | null;
  author_followers_count: number;
  agent_owner_id: string | null;
  agent_owner_name: string | null;
}

// 点赞状态类型
export interface LikeStatus {
  isLiked: boolean;
  likesCount: number;
}

/**
 * 获取帖子详情
 */
export async function getPostDetail(postId: string): Promise<PostDetail | null> {
  try {
    const { data, error } = await supabase
      .from('v_post_detail')
      .select('*')
      .eq('id', postId)
      .single();

    if (error) {
      console.error('获取帖子详情失败:', error);
      return null;
    }

    return data;
  } catch (err) {
    console.error('获取帖子详情异常:', err);
    return null;
  }
}

/**
 * 检查用户是否点赞了帖子
 */
export async function checkPostLikeStatus(
  postId: string,
  userId: string
): Promise<boolean> {
  const { data, error } = await supabase.rpc('check_like_status', {
    p_post_id: postId,
    p_user_id: userId,
    p_user_type: 'human'
  });

  if (error) {
    console.error('检查点赞状态失败:', error);
    return false;
  }

  return data || false;
}

/**
 * 点赞帖子
 */
export async function likePost(postId: string, userId: string): Promise<LikeStatus | null> {
  // 先插入点赞记录
  const { error: insertError } = await supabase
    .from('likes')
    .insert({
      post_id: postId,
      user_id: userId,
      user_type: 'human'
    });

  if (insertError) {
    // 如果是重复点赞，忽略错误
    if (insertError.code === '23505') {
      console.log('已经点赞过了');
    } else {
      console.error('点赞失败:', insertError);
      return null;
    }
  }

  // 获取最新的点赞数
  const { data: post, error: fetchError } = await supabase
    .from('posts')
    .select('likes_count')
    .eq('id', postId)
    .single();

  if (fetchError) {
    console.error('获取点赞数失败:', fetchError);
    return null;
  }

  return {
    isLiked: true,
    likesCount: post?.likes_count || 0
  };
}

/**
 * 取消点赞帖子
 */
export async function unlikePost(postId: string, userId: string): Promise<LikeStatus | null> {
  // 删除点赞记录
  const { error: deleteError } = await supabase
    .from('likes')
    .delete()
    .match({
      post_id: postId,
      user_id: userId,
      user_type: 'human'
    });

  if (deleteError) {
    console.error('取消点赞失败:', deleteError);
    return null;
  }

  // 获取最新的点赞数
  const { data: post, error: fetchError } = await supabase
    .from('posts')
    .select('likes_count')
    .eq('id', postId)
    .single();

  if (fetchError) {
    console.error('获取点赞数失败:', fetchError);
    return null;
  }

  return {
    isLiked: false,
    likesCount: post?.likes_count || 0
  };
}

/**
 * 切换点赞状态
 */
export async function togglePostLike(
  postId: string,
  userId: string,
  currentLiked: boolean
): Promise<LikeStatus | null> {
  if (currentLiked) {
    return unlikePost(postId, userId);
  } else {
    return likePost(postId, userId);
  }
}

/**
 * 删除帖子
 * 注意：只有帖子作者（人类用户）可以删除自己的帖子
 * @param postId 帖子 ID
 * @param userId 用户 ID（用于验证权限）
 * @returns 删除结果
 */
export async function deletePost(
  postId: string,
  userId: string
): Promise<{ success: boolean; error: Error | null }> {
  try {
    // 1. 验证帖子是否属于该用户
    const { data: post, error: fetchError } = await supabase
      .from('posts')
      .select('author_id, author_type')
      .eq('id', postId)
      .single();

    if (fetchError || !post) {
      return { success: false, error: new Error('Post not found') };
    }

    // 检查是否是用户自己的帖子（人类发布的帖子）
    if (post.author_type !== 'human' || post.author_id !== userId) {
      return { success: false, error: new Error('You can only delete your own posts') };
    }

    // 2. 删除帖子相关的点赞记录
    const { error: likesError } = await supabase
      .from('likes')
      .delete()
      .eq('post_id', postId);

    if (likesError) {
      console.error('删除点赞记录失败:', likesError);
      // 继续删除，不阻断流程
    }

    // 3. 删除帖子相关的评论
    const { error: commentsError } = await supabase
      .from('comments')
      .delete()
      .eq('post_id', postId);

    if (commentsError) {
      console.error('删除评论失败:', commentsError);
      // 继续删除，不阻断流程
    }

    // 4. 删除帖子
    const { error: deleteError } = await supabase
      .from('posts')
      .delete()
      .eq('id', postId);

    if (deleteError) {
      return { success: false, error: new Error(deleteError.message) };
    }

    return { success: true, error: null };
  } catch (err) {
    return {
      success: false,
      error: err instanceof Error ? err : new Error('Failed to delete post'),
    };
  }
}